import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const PUSH_REQUESTED_KEY = 'push_permission_requested';

/**
 * Registers push notification token with Supabase
 * when running inside a native Capacitor app.
 * 
 * On iOS: saves the raw APNs device token (64-char hex).
 *   The server sends directly via APNs HTTP/2.
 * On Android: saves the FCM registration token.
 *   The server sends via FCM HTTP v1.
 */
export const usePushNotifications = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user || !Capacitor.isNativePlatform()) return;

    // Skip on iPad compatibility mode — can hang/crash
    const isIPad = navigator.userAgent.includes('iPad') ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
      (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    if (isIPad) return;

    const saveToken = async (token: string) => {
      await supabase
        .from('profiles')
        .update({ push_token: token })
        .eq('user_id', user.id);
    };

    const setupPush = async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        const alreadyRequested = localStorage.getItem(PUSH_REQUESTED_KEY) === '1';

        if (!alreadyRequested) {
          localStorage.setItem(PUSH_REQUESTED_KEY, '1');
          const result = await PushNotifications.requestPermissions();
          if (result.receive !== 'granted') return;
        } else {
          const permStatus = await PushNotifications.checkPermissions();
          if (permStatus.receive !== 'granted') return;
        }

        await PushNotifications.register();

        // On both iOS and Android, save the token directly.
        // iOS returns raw APNs token (hex), Android returns FCM token.
        PushNotifications.addListener('registration', async (token) => {
          console.log(`[Push] Token received (${Capacitor.getPlatform()}): ${token.value.substring(0, 10)}...`);
          await saveToken(token.value);
        });

        PushNotifications.addListener('registrationError', (error) => {
          console.error('[Push] Registration error:', error);
        });

        // Handle incoming notifications while app is open
        PushNotifications.addListener('pushNotificationReceived', (_notification) => {
          // Foreground notification received
        });

        // Handle notification tap
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          const data = notification.notification.data;
          if (data?.postId) {
            window.location.href = `/post/${data.postId}`;
          } else if (data?.path) {
            window.location.href = data.path;
          }
        });
      } catch (err) {
        // Push notifications not available on this platform
      }
    };

    const timer = setTimeout(setupPush, 2000);
    return () => clearTimeout(timer);
  }, [user]);
};

/**
 * Hook to check push notification permission status.
 */
export const usePushPermissionStatus = () => {
  const [status, setStatus] = useState<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown');

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const check = async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const result = await PushNotifications.checkPermissions();
        setStatus(result.receive as any);
      } catch {
        setStatus('unknown');
      }
    };
    check();
  }, []);

  return status;
};

/**
 * Request push notification permission just-in-time.
 */
export const requestPushPermission = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const result = await PushNotifications.requestPermissions();
    if (result.receive === 'granted') {
      await PushNotifications.register();
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * Open the device's app settings so the user can manually enable notifications.
 */
export const openAppSettings = async () => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: 'app-settings:' });
    } catch {
      console.log('Cannot open app settings');
    }
  }
};
